import { useMemo } from 'react'

import { ModalContext } from '@domain-group/suit-theme'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import useCampaign from '@hooks/useCampaign'

import DownloadMediaModal from '@modals/DownloadMediaModal'

import handleCallbackAction, { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import PageContext from '@contexts/pageContext'

import type { CampaignModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showDownloadMediaModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    DownloadMediaModal: {
      closeModal: () => setState({ showDownloadMediaModal: false }),
      downloadImages: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateAgency: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const MediaReviewer = () => {
  const { campaignId } = useNumericParams()

  const { imageIds, publicToken, adCopyTypes } = parsePermittedQueryParams(
    getQueryParamsFromHash(),
    ['imageIds', 'publicToken', 'adCopyTypes'],
  )

  // @ts-expect-error TS2345
  const parsedImageIds = imageIds ? JSON.parse(imageIds) : []

  const [state, setState] = useSetState(defaultState)
  const { showDownloadMediaModal } = state

  useCampaign({ id: campaignId }, {
    performHttpRequests: !!campaignId,
    requestOptions: {
      include_agency_brand: true,
      include_agency_image: true,
      include_agent_image: true,
      include_agent_user: true,
      include_attachment_file: true,
      include_brand_public_link_cover_image: true,
      include_brand_public_link_logo: true,
      include_campaign_ad_copies: true,
      include_campaign_agency: true,
      include_campaign_agents: true,
      include_campaign_category: true,
      include_campaign_images: true,
      include_campaign_inspections: true,
      include_campaign_sale_method: true,
      include_campaign_team: true,
      include_image_id_codes: true,
      include_image_sizes: true,
      public_token: publicToken,
    },
  })

  const modalContext = useModals({ setState })
  const { callbacks: { setModalData } } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showDownloadMediaModal: (payload: { campaign: CampaignModel, selectedImageIds: number[] }) => {
        setModalData('DownloadMediaModal', payload)
        setState({ showDownloadMediaModal: true })
      },
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        {/*
         // @ts-expect-error TS2322 */}
        <PageContent adCopyTypes={adCopyTypes} imageIds={parsedImageIds} campaignId={campaignId} />

        <DownloadMediaModal
          // @ts-expect-error TS2322
          callbacks={callbacks('DownloadMediaModal', setState)}
          isPublicPage
          showModal={showDownloadMediaModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default MediaReviewer
